import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDialog } from "../components/dialog/DialogContext";
import TableTab from "../components/tabs/table-tab";
import { FILTER_COMMAND, MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../constants/command-status";
import Commands from "../constants/commands";
import DeviceStatus, { DEVICE_STATES2 } from "../constants/device-status";
import { HARDWARE_READERS } from "../constants/menu-types";
import { HardwareSubCmds } from "../constants/sub-commands";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { useIcaWebsocketContext } from "../contexts/websocket/IcaWebsocketContext";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/ReaderColumns';
import { ExecuteCommand } from "../models/ExecuteCommand";
import { ListQuery } from "../models/ListQuery";
import { LockModel } from "../models/Lock";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import { getDeviceTableLegendItems } from "../models/TableLegend";
import ControllerSearch from "../models/search/ControllerSearch";
import DoorSearch from "../models/search/DoorSearch";
import searchColumns from '../models/search/ReaderSearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getControllerNames } from "../services/controllerService";
import { getDoorNamesWithPagination } from "../services/doorService";
import { executeCommandForDevice } from "../services/executeCommandService";
import { getLockById, getLocks } from "../services/lockService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getErrorMessage } from "../services/utilities/ServiceError";
import { IAppState } from "../store/types";
import { getCommand, getDeviceStates, getWsDevice } from "../utilities/WebsocketUtil";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setBusyMessage: (message: string) => void,
}

const LocksTab: FC<Props> = ({ setBusy, categories, setCategories, setBusyMessage }) => {
    const { translate } = useLocale();
    const itemPath = '/access-control/hardware/lock/';
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const dialog = useDialog();
    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [updateTable, setUpdateTable] = useState(() => (updateMethod: (tableData: any[]) => any[]) => { return });
    const [locksStatusInfo, setLocksStatusInfo] = useState<{ controllerFixedAddress: string, readerFixedAddresses: string[] }>({
        readerFixedAddresses: [],
        controllerFixedAddress: ""
    });
    const icaWebSocket = useIcaWebsocketContext();
    let filterStatusCodesRequestTimeOut;

    // The device status codes this view is interesting in
    const filterStatusCodes = [DEVICE_STATES2.PERIPHERAL_STATE];

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'controller.deviceImprox.id',
            values: [],
            dataMethod: getControllerNames,
            searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'location.id',
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        }
        ]);
    }, [])

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            sendfilterStatuCodeRequest();
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            clearTimeout(filterStatusCodesRequestTimeOut);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState])

    useEffect(() => {
        updateTable((tableData) => {
            return updateLockState(locksStatusInfo.readerFixedAddresses, tableData, locksStatusInfo.controllerFixedAddress)
        })
    }, [locksStatusInfo])

    const initialiseUserPreferences = (setDetails: (userLoginId, HARDWARE_READERS) => void) => {
        setDetails(userLoginId, HARDWARE_READERS); // TODO add a preference id for locks
    }

    const updateLockState = (lockFixedAddresses: string[], locks: LockModel[], controllerFixedAddress: String) => {
        const locksForThisController = locks.filter((lock: LockModel) => lock.controllerAddress === controllerFixedAddress);

        locksForThisController.forEach((lock: LockModel) => {
            let status = DeviceStatus.Unknown.id;

            if (lockFixedAddresses.includes(lock.fixedAddress ?? '')) {
                status = DeviceStatus.Online.id;
            }
            else if (!lockFixedAddresses.includes(lock.controllerAddress ?? '')) {
                status = DeviceStatus.Offline.id;
            }
            else if (!lock.enabled) {
                status = DeviceStatus.Disabled.id;
            }

            // Update the status of the lock if it has changed
            if (lock.status !== status) {
                const lockToUpdate = locks.find((l: LockModel) => l.id === lock.id);
                if (lockToUpdate) {
                    lockToUpdate.status = status;
                }
            }
        });

        return locks;
    }

    const sendfilterStatuCodeRequest = () => {
        // Tell the server to only report on these device status codes.
        const filterStatusCodesRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "statusCodeFilters": {
                    "includeStatusCodes": filterStatusCodes
                }
            }
        }

        filterStatusCodesRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterStatusCodesRequest))
        }, 5000)
    }

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            if (getCommand(response) === MONITORING_COMMAND.MONITORING_DEVICE) {
                const deviceStates = getDeviceStates(response);

                if (Array.isArray(deviceStates)) {
                    deviceStates.forEach((deviceState) => {
                        const wsDevice = getWsDevice(deviceState);

                        // Peripherals (online readers attached to the controller)
                        if (wsDevice.fixedAddress) {
                            const readerFixedAddresses = wsDevice.peripheralAddresses ? wsDevice.peripheralAddresses : [];
                            setLocksStatusInfo({
                                readerFixedAddresses: readerFixedAddresses,
                                controllerFixedAddress: wsDevice.fixedAddress
                            })
                        }

                    });
                }
            }
        }
    }

    const tableDataUpdate = (updateTableMethod: (updateMethod: (tableData: any[]) => any[]) => void) => {
        setUpdateTable(() => updateTableMethod);
    }

    const onDelete = async (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.unstallingMessage))

        let fixedAddress: string | undefined;

        try {
            const reader = await getLockById(id);
            fixedAddress = reader.fixedAddress
        } catch (err) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.deviceNotFound) })
        }
        const command: ExecuteCommand = {
            cmd: Commands.Engine.label,
            cmdSubType: HardwareSubCmds.Uninstall_Reader,
            userLoginId: user.id,
            fixedAddress: fixedAddress,
            jsonResponse: ""
        }

        return new Promise((resolve, reject) => {
            executeCommandForDevice(command)
                .then(
                    (deleteResult: any) => {
                        resolve(deleteResult);
                        const newCatergories: Category[] = categories.map((category) => {
                            return category.id === 4 ? { ...category, count: (category?.count ?? 1) - 1 } : category;
                        })
                        setCategories(newCatergories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    }).catch(error => {
                        dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                    })
        });
    }

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={(listParameters?: ListQuery) => getLocks(translate, listParameters)}
        deleteMethod={onDelete}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        legendItems={getDeviceTableLegendItems(translate)}
        useTable
        useLegend
        useSearch
        useFilter
        usePagination
        useSort
        tableDataUpdate={tableDataUpdate}
        lookup={lookup}
        showAddNewButton={false}>
        <TableTab
            title={translate.byKey(TranslationKeys.locks)}
            selectRows={false}
            rowPath={itemPath}
            cyName="locks" />
    </TableContextProvider>
}

export default LocksTab;
